"use client";
import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";
import bitcoin from "@/public/career/bitcoin.png";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent, richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { motion } from "framer-motion";
import { useRef } from "react";

export default function WhyUsSection({ data }: { data: PageCareerSchema }) {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  return (
    <section className="relative">
      <div className="flex relative flex-col gap-[6rem] w-[min(94.375rem,100%)] mx-auto">
        <div className="container flex relative z-10 flex-col lg:items-center px-[2.25rem]">
          {data?.whyUsChip && (
            <p className="font-medium font-geist text-black/70 text-[2.25rem] lg:text-[2rem] mb-[1.75rem] leading-[150%]">
              {data.whyUsChip}
            </p>
          )}

          {data?.whyUsText && (
            <div className="hidden flex-col items-center lg:flex richtext prose-strong:text-black prose-strong:font-[400] prose-headings:text-[7rem] prose-headings:mb-[2.5rem]">
              <PortableText
                value={data.whyUsText}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="text-[2.25rem] font-[500] font-geist leading-[140%] text-[#103037]/80 mb-0 tracking-[-0.045rem] capitalize w-[min(56.375rem,100%)]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            </div>
          )}

          {data?.whyUsTextMobile && (
            <div className="flex flex-col items-start w-full lg:hidden richtext lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] prose-p:text-[2.25rem] prose-strong:text-black prose-strong:font-[400] prose-headings:text-[5rem] prose-headings:mb-[2.5rem] prose-headings:text-start lg:prose-headings:mb-[1.5rem]">
              <PortableText
                value={data.whyUsTextMobile}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="text-[2.25rem] font-[500] font-geist leading-[140%] text-[#103037]/80 mb-0 tracking-[-0.045rem] capitalize w-[min(56.375rem,100%)] text-start">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            </div>
          )}
        </div>

        {data?.whyUsCards && (
          <div className="flex flex-col items-center lg:px-0 gap-[1.5rem] px-[2rem]">
            {data.whyUsCards.map((item, index) => (
              <motion.div
                key={index}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className="flex flex-row border gap-[2.25rem] p-[3rem] rounded-[3rem] border-[#95B0A1]/25">
                <Image
                  className="!w-[5rem] !h-[5rem] flex-shrink-0"
                  src={item.image}
                  alt={item.title}
                  width={100}
                  height={100}
                />
                <div className="flex flex-col gap-[1.13rem] mt-[.6rem]">
                  <p className="mb-0 text-[2.5rem] text-[#1C1F22] font-[500] font-geist">{item.title}</p>
                  <p className="text-[2.25rem] lg:text-[2rem] font-geist leading-[150%] text-[#1C1F22]/80 mb-0">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
